import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import Cookies from 'js-cookie'
import axios from 'axios'
import apiClient, { getBaseURL } from '../../utils/axios'

const initialState = {
  user: null,
  tenant: null,
  accessToken: Cookies.get('access_token') || null,
  refreshToken: Cookies.get('refresh_token') || null,
  isAuthenticated: !!Cookies.get('access_token'),
  isLoading: false,
  error: null,
}

// Async thunks
export const login = createAsyncThunk(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await apiClient.post('/auth/login', credentials)
      
      if (response.data.success) {
        const { access_token, refresh_token, user, tenant } = response.data.data
        
        // Store tokens in cookies
        Cookies.set('access_token', access_token, { expires: 1 / 24 }) // 1 hour
        Cookies.set('refresh_token', refresh_token, { expires: 30 }) // 30 days
        
        return { access_token, refresh_token, user, tenant }
      } else {
        return rejectWithValue(response.data.message || 'Login failed')
      }
    } catch (error) {
      // Extract error message from response
      let errorMessage = 'Login failed. Please try again.'
      
      // Check if error has a response (HTTP error)
      if (error.response) {
        const responseData = error.response.data
        
        // Try to get message from response data
        if (responseData) {
          errorMessage = responseData.message || 
                        responseData.error || 
                        (typeof responseData === 'string' ? responseData : errorMessage)
        }
      } else if (error.message) {
        // Network error or other error
        errorMessage = error.message
      }
      
      // Ensure errorMessage is a string and not empty
      errorMessage = String(errorMessage || 'Login failed. Please try again.').trim()
      
      // Log for debugging (remove in production if needed)
      if (process.env.NODE_ENV === 'development') {
        console.error('Login error:', {
          error,
          errorMessage,
          responseData: error.response?.data,
        })
        console.log('Rejecting with value:', errorMessage)
      }
      
      return rejectWithValue(errorMessage)
    }
  }
)

export const checkAuth = createAsyncThunk(
  'auth/checkAuth',
  async (_, { rejectWithValue }) => {
    try {
      const accessToken = Cookies.get('access_token')
      
      if (!accessToken) {
        return rejectWithValue('No access token found')
      }

      const response = await apiClient.get('/user/profile')
      
      if (response.data.success) {
        return {
          tenant: response.data.data.tenant,
          users: response.data.data.users,
        }
      } else {
        return rejectWithValue('Authentication failed')
      }
    } catch (error) {
      // If 401, try to refresh token
      if (error.response?.status === 401) {
        const refreshTokenValue = Cookies.get('refresh_token')
        
        if (refreshTokenValue) {
          try {
            const refreshResponse = await axios.post(
              `${getBaseURL()}/auth/refresh`,
              { refresh_token: refreshTokenValue },
              { headers: { 'Content-Type': 'application/json' } }
            )
            
            if (refreshResponse.data.success) {
              const { access_token, refresh_token } = refreshResponse.data.data
              
              Cookies.set('access_token', access_token, { expires: 1 / 24 })
              Cookies.set('refresh_token', refresh_token, { expires: 30 })
              
              // Retry the profile request
              const retryResponse = await apiClient.get('/user/profile')
              if (retryResponse.data.success) {
                return {
                  tenant: retryResponse.data.data.tenant,
                  users: retryResponse.data.data.users,
                  access_token,
                  refresh_token,
                }
              }
            }
          } catch (refreshError) {
            return rejectWithValue('Token refresh failed')
          }
        }
      }
      
      return rejectWithValue(error.response?.data?.message || 'Authentication failed')
    }
  }
)

export const refreshToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, { rejectWithValue }) => {
    try {
      const refreshTokenValue = Cookies.get('refresh_token')
      
      if (!refreshTokenValue) {
        return rejectWithValue('No refresh token found')
      }

      const response = await axios.post(
        `${getBaseURL()}/auth/refresh`,
        { refresh_token: refreshTokenValue },
        { headers: { 'Content-Type': 'application/json' } }
      )
      
      if (response.data.success) {
        const { access_token, refresh_token } = response.data.data
        
        Cookies.set('access_token', access_token, { expires: 1 / 24 })
        Cookies.set('refresh_token', refresh_token, { expires: 30 })
        
        return { access_token, refresh_token }
      } else {
        return rejectWithValue('Token refresh failed')
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Token refresh failed'
      )
    }
  }
)

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.user = null
      state.tenant = null
      state.accessToken = null
      state.refreshToken = null
      state.isAuthenticated = false
      state.error = null
      
      // Clear cookies
      Cookies.remove('access_token')
      Cookies.remove('refresh_token')
    },
    setUser: (state, action) => {
      state.user = action.payload
    },
    clearError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false
        state.isAuthenticated = true
        state.accessToken = action.payload.access_token
        state.refreshToken = action.payload.refresh_token
        state.user = action.payload.user
        state.tenant = action.payload.tenant
        state.error = null
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false
        state.isAuthenticated = false
        // Ensure error is a string
        const errorMessage = action.payload ? String(action.payload) : (action.error?.message || 'Login failed. Please try again.')
        state.error = errorMessage
        
        // Debug log in development
        if (process.env.NODE_ENV === 'development') {
          console.log('Login rejected - setting error in state:', {
            payload: action.payload,
            error: action.error,
            errorMessage,
            stateError: state.error,
          })
        }
      })
      // Check Auth
      .addCase(checkAuth.pending, (state) => {
        state.isLoading = true
      })
      .addCase(checkAuth.fulfilled, (state, action) => {
        state.isLoading = false
        state.isAuthenticated = true
        state.tenant = action.payload.tenant
        if (action.payload.access_token) {
          state.accessToken = action.payload.access_token
        }
        if (action.payload.refresh_token) {
          state.refreshToken = action.payload.refresh_token
        }
      })
      .addCase(checkAuth.rejected, (state) => {
        state.isLoading = false
        state.isAuthenticated = false
        state.user = null
        state.tenant = null
        Cookies.remove('access_token')
        Cookies.remove('refresh_token')
      })
      // Refresh Token
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.accessToken = action.payload.access_token
        state.refreshToken = action.payload.refresh_token
      })
      .addCase(refreshToken.rejected, (state) => {
        state.isAuthenticated = false
        state.user = null
        state.tenant = null
        Cookies.remove('access_token')
        Cookies.remove('refresh_token')
      })
  },
})

export const { logout, setUser, clearError } = authSlice.actions
export default authSlice.reducer

